import React from 'react'
import { Field, Form, Formik, FormikErrors } from 'formik'
import { Navigate } from 'react-router-dom'
import clsx from 'clsx'
import * as Yup from 'yup'

import { Button } from 'shadcdn/ui/button'
import { Spinner } from 'ui/Spinner/Spinner'

import { RegistrationData, useAuthStore } from '../useAuthStore'

type Props = {
  className?: string
  messageToUser?: React.ReactNode
  onClickSignIn?: () => void
  onSuccess?: () => void
}

const SignupSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Please enter your email'),
  password: Yup.string().required('Please enter password'),
  passwordConfirmation: Yup.string()
    .required('Please confirm password')
    .oneOf([Yup.ref('password'), ''], "Passwords doesn't match"),
})

const FormError = ({ error }: { error?: string }) => {
  if (!error) return null
  return <p className="text-xs text-red-600 mt-2">{error}</p>
}

export const SignUpForm = React.memo((props: Props) => {
  const { onClickSignIn, onSuccess, messageToUser } = props
  const signUp = useAuthStore((state) => state.signUp)
  // const [errors, setErrors] = useState<Errors>({})

  const onSubmit = async (
    data: RegistrationData,
    { setErrors }: { setErrors: (errors: FormikErrors<any>) => void }
  ) => {
    const response = await signUp(data)
    if (!response.success) {
      setErrors(response.errors)
      return
    }
    onSuccess?.()
  }

  const user = useAuthStore((state) => state.user)
  if (user) {
    return <Navigate to="/" replace={true} />
  }

  return (
    <div className="w-80">
      <h1 className="block text-2xl font-bold text-gray-800">Sign up</h1>

      <div className="mt-5">
        <Formik<RegistrationData>
          onSubmit={onSubmit}
          initialValues={{ email: '', password: '', passwordConfirmation: '' }}
          validationSchema={SignupSchema}
          validateOnChange={false}
          validateOnMount={false}
        >
          {({ errors, touched, isSubmitting, submitCount }) => {
            const submitted: boolean = submitCount > 0
            return (
              <Form>
                <div className="grid gap-y-4">
                  {messageToUser && (
                    <div className="text-sm border-l-2 bg-gray-100 p-2 px-4 rounded text-gray-600">{messageToUser}</div>
                  )}
                  <div>
                    <label htmlFor="email" className="block text-sm mb-2">
                      Email address
                    </label>
                    <div className="relative">
                      <Field
                        name="email"
                        type="text"
                        className={clsx(
                          'py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-slate-300 focus:ring-slate-200 ring-0',
                          {
                            'focus:border-red-300 focus:ring-red-200 border-red-600':
                              submitted && errors.email && touched.email,
                          }
                        )}
                      />
                      <div className="text-xs mt-1 text-gray-500">We'll send a confirmation link</div>
                    </div>
                    {submitted && touched.email && <FormError error={errors.email} />}
                  </div>

                  <div>
                    <div className="flex justify-between items-center">
                      <label htmlFor="password" className="block text-sm mb-2">
                        Password
                      </label>
                    </div>
                    <div className="relative">
                      <Field
                        type="password"
                        name="password"
                        className={clsx(
                          'py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-slate-300 focus:ring-slate-200 ring-0',
                          {
                            'focus:border-red-300 focus:ring-red-200 border-red-600':
                              submitted && errors.password && touched.password,
                          }
                        )}
                      />
                    </div>
                    {submitted && touched.password && <FormError error={errors.password} />}
                  </div>

                  <div>
                    <div className="flex justify-between items-center">
                      <label htmlFor="password" className="block text-sm mb-2">
                        Password Confirmation
                      </label>
                    </div>
                    <div className="relative">
                      <Field
                        type="password"
                        name="passwordConfirmation"
                        className={clsx(
                          'py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-slate-300 focus:ring-slate-200 ring-0',
                          {
                            'focus:border-red-300 focus:ring-red-200 border-red-600':
                              submitted && errors.passwordConfirmation && touched.passwordConfirmation,
                          }
                        )}
                      />
                    </div>
                    {submitted && touched.passwordConfirmation && <FormError error={errors.passwordConfirmation} />}
                  </div>

                  <Button disabled={isSubmitting}>
                    {isSubmitting && <Spinner sizeClassName="w-3 h-3 mr-1 -mt-1" />}
                    Sign Up
                  </Button>
                </div>

                <div className="py-3 flex items-center text-xs text-gray-400 uppercase before:flex-[1_1_0%] before:border-t before:border-gray-100 before:mr-6 after:flex-[1_1_0%] after:border-t after:border-gray-200 after:ml-6 ">
                  Or
                </div>

                <p className=" text-sm text-gray-500  text-center">
                  <a
                    className="text-slate-900 decoration-2 underline font-medium cursor-pointer"
                    onClick={() => onClickSignIn?.()}
                  >
                    Sign in
                  </a>{' '}
                  if you have an account
                </p>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
})
